// Behaviour for sections/neuracoil-connectivity.liquid (loaded with defer).
(() => {
  const section = document.getElementById('neuracoil-workflow');
  if (!section) return;

  const cards = Array.from(section.querySelectorAll('[data-workflow-card]'));
  const triggers = Array.from(section.querySelectorAll('[data-workflow-trigger]'));
  const mobileDetails = Array.from(section.querySelectorAll('[data-workflow-mobile-detail]'));
  const closeButtons = Array.from(section.querySelectorAll('[data-workflow-close]'));
  const visual = section.querySelector('.neuracoil-connectivity__visual');
  const productDevice = section.querySelector('.neuracoil-connectivity__device--product');
  const cable = section.querySelector('.neuracoil-connectivity__cable--vst');
  const appWaves = section.querySelector('.neuracoil-connectivity__waves--app');
  const controlsWaves = section.querySelector('.neuracoil-connectivity__waves--controls');
  const workflowCards = {
    app: section.querySelector('[data-workflow-card="app"]'),
    studio: section.querySelector('[data-workflow-card="studio"]'),
    stage: section.querySelector('[data-workflow-card="stage"]'),
  };
  let shapeFrame = null;
  let switchFrame = null;
  let entryTimer = null;
  let connectionFrame = null;
  const interactiveSelector = 'a, button, input, select, textarea, summary, [role="button"], [tabindex]:not([tabindex="-1"])';
  const pickupVisibleBox = {
    left: 0.071875,
    right: 0.928125,
    top: 0.138889,
    bottom: 0.859722,
  };

  const cssPx = (value) => `${Math.round(value * 10) / 10}px`;
  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const clearConnectionGeometry = () => {
    [
      '--connectivity-cable-top',
      '--connectivity-cable-height',
      '--connectivity-app-waves-left',
      '--connectivity-app-waves-top',
      '--connectivity-app-waves-width',
      '--connectivity-app-waves-height',
      '--connectivity-app-waves-rotation',
      '--connectivity-controls-waves-left',
      '--connectivity-controls-waves-top',
      '--connectivity-controls-waves-width',
      '--connectivity-controls-waves-height',
      '--connectivity-controls-waves-rotation',
      '--connectivity-cable-width',
    ].forEach((property) => section.style.removeProperty(property));
  };

  const relativeRect = (element, parentRect) => {
    if (!element) return null;
    const rect = element.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;

    return {
      top: rect.top - parentRect.top,
      right: rect.right - parentRect.left,
      bottom: rect.bottom - parentRect.top,
      left: rect.left - parentRect.left,
      width: rect.width,
      height: rect.height,
    };
  };

  const measuredPickup = (rect) => ({
    left: rect.left + rect.width * pickupVisibleBox.left,
    right: rect.left + rect.width * pickupVisibleBox.right,
    top: rect.top + rect.height * pickupVisibleBox.top,
    bottom: rect.top + rect.height * pickupVisibleBox.bottom,
  });

  const pointOnLine = (from, to, t) => ({
    x: from.x + (to.x - from.x) * t,
    y: from.y + (to.y - from.y) * t,
  });

  const segmentRectIntersection = (from, to, rect) => {
    const candidates = [];
    const dx = to.x - from.x;
    const dy = to.y - from.y;

    const addCandidate = (t) => {
      if (!Number.isFinite(t) || t <= 0 || t > 1) return;
      const point = pointOnLine(from, to, t);
      const withinX = point.x >= rect.left - 0.5 && point.x <= rect.right + 0.5;
      const withinY = point.y >= rect.top - 0.5 && point.y <= rect.bottom + 0.5;
      if (withinX && withinY) candidates.push({ point, t });
    };

    if (dx) {
      addCandidate((rect.left - from.x) / dx);
      addCandidate((rect.right - from.x) / dx);
    }

    if (dy) {
      addCandidate((rect.top - from.y) / dy);
      addCandidate((rect.bottom - from.y) / dy);
    }

    candidates.sort((a, b) => a.t - b.t);
    return candidates[0]?.point || to;
  };

  const setWaveGeometry = ({ prefix, cardCorner, pickup, pickupCenter }) => {
    const pickupEdge = segmentRectIntersection(cardCorner, pickupCenter, pickup);
    const midpoint = {
      x: (cardCorner.x + pickupEdge.x) / 2,
      y: (cardCorner.y + pickupEdge.y) / 2,
    };
    const distance = Math.hypot(pickupEdge.x - cardCorner.x, pickupEdge.y - cardCorner.y);
    const width = clamp(distance * 0.36, 76, 96);
    const height = width * 1.12;
    const rotation = Math.atan2(pickupCenter.y - cardCorner.y, pickupCenter.x - cardCorner.x) * 180 / Math.PI;
    const visualRotation = prefix === 'app'
      ? clamp(rotation, 10, 32)
      : clamp(rotation, 148, 170);

    section.style.setProperty(`--connectivity-${prefix}-waves-left`, cssPx(midpoint.x));
    section.style.setProperty(`--connectivity-${prefix}-waves-top`, cssPx(midpoint.y));
    section.style.setProperty(`--connectivity-${prefix}-waves-width`, cssPx(width));
    section.style.setProperty(`--connectivity-${prefix}-waves-height`, cssPx(height));
    section.style.setProperty(`--connectivity-${prefix}-waves-rotation`, `${Math.round(visualRotation * 10) / 10}deg`);
  };

  const updateConnectionGeometry = () => {
    connectionFrame = null;
    if (!window.matchMedia('(min-width: 990px)').matches || !visual || !productDevice) {
      clearConnectionGeometry();
      return;
    }

    const visualRect = visual.getBoundingClientRect();
    const productRect = relativeRect(productDevice, visualRect);
    const appRect = relativeRect(workflowCards.app, visualRect);
    const studioRect = relativeRect(workflowCards.studio, visualRect);
    const stageRect = relativeRect(workflowCards.stage, visualRect);

    if (!productRect || !appRect || !studioRect || !stageRect) {
      clearConnectionGeometry();
      return;
    }

    const pickup = measuredPickup(productRect);
    const pickupCenter = {
      x: (pickup.left + pickup.right) / 2,
      y: (pickup.top + pickup.bottom) / 2,
    };

    if (appWaves) {
      setWaveGeometry({
        prefix: 'app',
        cardCorner: { x: appRect.right, y: appRect.bottom },
        pickup,
        pickupCenter,
      });
    }

    if (controlsWaves) {
      setWaveGeometry({
        prefix: 'controls',
        cardCorner: { x: stageRect.left, y: stageRect.bottom },
        pickup,
        pickupCenter,
      });
    }

    if (cable) {
      const cableTop = studioRect.bottom + 6;
      const cableBottom = pickup.top - 5;
      const cableHeight = Math.max(118, cableBottom - cableTop);
      const cableWidth = clamp(cableHeight * 0.155, 30, 44);

      section.style.setProperty('--connectivity-cable-top', cssPx(cableBottom - cableHeight));
      section.style.setProperty('--connectivity-cable-height', cssPx(cableHeight));
      section.style.setProperty('--connectivity-cable-width', cssPx(cableWidth));
    }
  };

  const queueConnectionUpdate = () => {
    if (connectionFrame) cancelAnimationFrame(connectionFrame);
    connectionFrame = requestAnimationFrame(updateConnectionGeometry);
  };

  const roundedTabSurfacePath = ({ width, height, tabLeft, tabRight, mainTop }) => {
    const radius = 24;
    const joinRadius = 46;
    const safeWidth = Math.max(width, 1);
    const safeHeight = Math.max(height, 1);
    const top = Math.max(mainTop, joinRadius + radius + 4);
    const left = Math.max(0, tabLeft);
    const right = Math.min(safeWidth, tabRight);
    const atLeftEdge = left <= 2;
    const atRightEdge = safeWidth - right <= 2;
    const commands = [];
    const curve = (x1, y1, x, y) => `Q ${x1} ${y1} ${x} ${y}`;

    const topRightCorner = `H ${safeWidth - radius} ${curve(safeWidth, top, safeWidth, top + radius)}`;
    const bottomAndLeft = `V ${safeHeight - radius} ${curve(safeWidth, safeHeight, safeWidth - radius, safeHeight)} H ${radius} ${curve(0, safeHeight, 0, safeHeight - radius)}`;

    if (atLeftEdge) {
      commands.push(
        `M ${radius} 0`,
        `H ${right - radius}`,
        curve(right, 0, right, radius),
        `V ${top - joinRadius}`,
        curve(right, top, right + joinRadius, top),
        topRightCorner,
        bottomAndLeft,
        `V ${radius}`,
        curve(0, 0, radius, 0),
        'Z'
      );
    } else if (atRightEdge) {
      commands.push(
        `M ${radius} ${top}`,
        `H ${left - joinRadius}`,
        curve(left, top, left, top - joinRadius),
        `V ${radius}`,
        curve(left, 0, left + radius, 0),
        `H ${safeWidth - radius}`,
        curve(safeWidth, 0, safeWidth, radius),
        bottomAndLeft,
        `V ${top + radius}`,
        curve(0, top, radius, top),
        'Z'
      );
    } else {
      commands.push(
        `M ${radius} ${top}`,
        `H ${left - joinRadius}`,
        curve(left, top, left, top - joinRadius),
        `V ${radius}`,
        curve(left, 0, left + radius, 0),
        `H ${right - radius}`,
        curve(right, 0, right, radius),
        `V ${top - joinRadius}`,
        curve(right, top, right + joinRadius, top),
        topRightCorner,
        bottomAndLeft,
        `V ${top + radius}`,
        curve(0, top, radius, top),
        'Z'
      );
    }

    return commands.join(' ');
  };

  const updateActiveShape = () => {
    shapeFrame = null;
    if (!window.matchMedia('(min-width: 990px)').matches) return;

    const activeCard = section.querySelector('[data-workflow-card].is-active');
    if (!activeCard) return;

    const svg = activeCard.querySelector('.neuracoil-connectivity__active-shape');
    const path = activeCard.querySelector('.neuracoil-connectivity__active-shape-path');
    const header = activeCard.querySelector('.neuracoil-connectivity__panel-header');
    if (!svg || !path || !header) return;

    const panelRect = activeCard.getBoundingClientRect();
    const headerRect = header.getBoundingClientRect();
    const width = Math.round(panelRect.width);
    const height = Math.round(panelRect.height);
    const tabLeft = Math.round(headerRect.left - panelRect.left);
    const tabRight = Math.round(headerRect.right - panelRect.left);
    const mainTop = Math.round(headerRect.bottom - panelRect.top + 26);

    svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
    path.setAttribute('d', roundedTabSurfacePath({ width, height, tabLeft, tabRight, mainTop }));
    activeCard.classList.add('is-shape-ready');
  };

  const queueShapeUpdate = () => {
    if (shapeFrame) cancelAnimationFrame(shapeFrame);
    shapeFrame = requestAnimationFrame(updateActiveShape);
  };

  const setActive = (workflow) => {
    const previousWorkflow = section.dataset.activeWorkflow || '';
    const isOpeningDetail = Boolean(workflow && !previousWorkflow);
    const isTabSwitch = Boolean(workflow && previousWorkflow && previousWorkflow !== workflow);

    if (shapeFrame) cancelAnimationFrame(shapeFrame);
    if (switchFrame) cancelAnimationFrame(switchFrame);
    if (entryTimer) clearTimeout(entryTimer);
    if (connectionFrame) cancelAnimationFrame(connectionFrame);
    shapeFrame = null;
    switchFrame = null;
    entryTimer = null;
    connectionFrame = null;

    section.classList.toggle('is-switching', isTabSwitch);
    section.classList.toggle('is-entering-detail', isOpeningDetail);
    cards.forEach((card) => card.classList.remove('is-shape-ready'));

    section.classList.toggle('is-detail-open', Boolean(workflow));
    if (workflow) section.dataset.activeWorkflow = workflow;
    else delete section.dataset.activeWorkflow;

    cards.forEach((card) => {
      const isActive = card.dataset.workflowCard === workflow;
      card.classList.toggle('is-active', isActive);
      card.classList.toggle('is-muted', Boolean(workflow) && !isActive);
    });

    triggers.forEach((trigger) => {
      const isActive = trigger.dataset.workflowTrigger === workflow;
      trigger.setAttribute('aria-expanded', String(isActive));
    });

    mobileDetails.forEach((detail) => {
      const isActive = detail.dataset.workflowMobileDetail === workflow;
      detail.classList.toggle('is-open', isActive);
      detail.setAttribute('aria-hidden', String(!isActive));
    });

    if (workflow) {
      updateActiveShape();
      queueShapeUpdate();
    }

    if (isTabSwitch) {
      switchFrame = requestAnimationFrame(() => {
        switchFrame = requestAnimationFrame(() => {
          section.classList.remove('is-switching');
          switchFrame = null;
        });
      });
    } else {
      section.classList.remove('is-switching');
    }

    if (isOpeningDetail) {
      entryTimer = setTimeout(() => {
        section.classList.remove('is-entering-detail');
        entryTimer = null;
      }, 300);
    } else {
      section.classList.remove('is-entering-detail');
    }

    queueConnectionUpdate();
  };

  triggers.forEach((trigger) => {
    trigger.addEventListener('click', (event) => {
      const workflow = trigger.dataset.workflowTrigger;
      const nextWorkflow = section.dataset.activeWorkflow === workflow ? '' : workflow;
      setActive(nextWorkflow);
      if (event.detail > 0) trigger.blur();
    });
  });

  cards.forEach((card) => {
    card.addEventListener('click', (event) => {
      if (event.target.closest(interactiveSelector)) return;
      if (event.target.closest('[data-workflow-mobile-detail]')) return;

      const workflow = card.dataset.workflowCard;
      if (workflow && section.dataset.activeWorkflow !== workflow) {
        setActive(workflow);
      }
    });
  });

  closeButtons.forEach((button) => {
    button.addEventListener('click', () => setActive(''));
  });

  document.addEventListener('click', (event) => {
    if (!section.classList.contains('is-detail-open')) return;
    const clickedCard = event.target.closest('[data-workflow-card]');
    if (clickedCard && section.contains(clickedCard)) return;

    setActive('');
  });

  section.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && section.classList.contains('is-detail-open')) {
      setActive('');
    }
  });

  const queueLayoutUpdate = () => {
    queueShapeUpdate();
    queueConnectionUpdate();
  };

  window.addEventListener('resize', queueLayoutUpdate, { passive: true });
  section.querySelectorAll('img').forEach((image) => {
    if (!image.complete) image.addEventListener('load', queueConnectionUpdate, { once: true });
  });

  if ('ResizeObserver' in window) {
    const observer = new ResizeObserver(queueLayoutUpdate);
    observer.observe(section);
    cards.forEach((card) => observer.observe(card));
  }

  queueConnectionUpdate();
})();
